/**
 * Fleet Mode Phase 9 — client-side accessibility resolver for map markers.
 *
 * Maps each charger's `fleet_access_mode` (the server-computed
 * deriveChargerAccessMode classification from @evi/shared) PLUS the
 * current driver's mode + fleet attribution into a three-state visual
 * treatment for the PWA map:
 *
 *   'accessible'     — driver's current mode + fleet pass the Phase 2
 *                      access gate's ALLOW branches; marker renders
 *                      with full color, tap proceeds to PreSession.
 *   'mode_mismatch'  — driver COULD use this charger if they switched
 *                      mode (mirrors MUST_SWITCH_TO_FLEET /
 *                      MUST_SWITCH_TO_PUBLIC). Marker is muted; tap
 *                      info-card surfaces a "Switch mode" banner.
 *   'inaccessible'   — driver can't use this charger regardless of
 *                      mode (different fleet on fleet_only, no fleet
 *                      attribution on fleet_only, etc.). Marker
 *                      heavily muted; tap info-card disables Start
 *                      with "Not available to your account" copy.
 *
 * This mirrors the Phase 2 resolveSessionAccess gate but is INTENTIONALLY
 * NOT a direct call into it — the server helper needs the full Fleet
 * doc (allows_mixed_use_default) to resolve the access mode, which the
 * PWA doesn't carry per-charger. The server already does that work and
 * surfaces fleet_access_mode on each embedded charger; this resolver
 * just consumes the result.
 *
 * The predicate matches the gate's decision table for the four
 * driver-mode × charger-mode × same-fleet combinations the map cares
 * about. The "inaccessible" branch never produces a mode-switch
 * remediation because no switch would change the answer.
 */
export type ChargerAccessibility = 'accessible' | 'mode_mismatch' | 'inaccessible';

export interface MapChargerInput {
  fleet_id: string | null | undefined;
  fleet_access_mode: 'public' | 'fleet_only' | 'mixed' | undefined;
  /**
   * Fleet Mode map-visibility extension — when the server returns
   * `access_mode: 'guest_closed'`, the charger is visible on the map
   * only because the caller requested public-mode visibility
   * (`current_mode=public`) and the server widened the cross-tenant
   * filter beyond is_open_to_guests=true. The driver can't actually
   * charge there (the operator hasn't opted in to guest access), so
   * the marker should render as 'inaccessible' regardless of
   * fleet_access_mode classification. This field is optional because
   * (a) older PWA builds may not pass it, (b) own-tenant /
   * own-pass-list chargers carry 'tenant' / 'granted' / 'guest' here
   * — only the new 4th value triggers the early short-circuit below.
   */
  access_mode?: 'tenant' | 'guest' | 'granted' | 'guest_closed';
}

export interface MapUserInput {
  fleet_id: string | null | undefined;
  current_mode: 'fleet' | 'public';
}

export function resolveChargerAccessibility(
  charger: MapChargerInput,
  user: MapUserInput,
): ChargerAccessibility {
  // Fleet Mode map-visibility extension — `guest_closed` short-
  // circuits to 'inaccessible'. The fleet_access_mode classification
  // doesn't apply here: the charger may be 'public' (no fleet
  // attribution) but the operator hasn't opted in to guest charging,
  // so the existing classifier would incorrectly return 'accessible'.
  // Early branch ensures the marker renders as muted + grayscale so
  // the driver sees "this exists in the network but I can't use it
  // here."
  if (charger.access_mode === 'guest_closed') return 'inaccessible';

  // Default to 'public' when the server hasn't surfaced a value
  // (older API versions, pre-Phase-9 data) — same conservative
  // fallback the gate makes when chargerFleet is null.
  const mode = charger.fleet_access_mode ?? 'public';
  const userInFleet = Boolean(user.fleet_id);
  const sameFleet =
    userInFleet
    && Boolean(charger.fleet_id)
    && String(user.fleet_id) === String(charger.fleet_id);

  switch (mode) {
    case 'public': {
      // Mirrors gate rows 1-3. Public chargers accept any driver in
      // 'public' mode. A fleet driver in 'fleet' mode can't bill a
      // public charger to their fleet → mode_mismatch (a one-tap
      // switch to public mode fixes it).
      if (userInFleet && user.current_mode === 'fleet') return 'mode_mismatch';
      return 'accessible';
    }

    case 'fleet_only': {
      // Mirrors gate rows 4-8. Only same-fleet drivers in 'fleet'
      // mode pass. Same-fleet driver in public mode → mode_mismatch
      // (switch to fleet fixes it). Everyone else → inaccessible.
      if (!userInFleet) return 'inaccessible';
      if (!sameFleet) return 'inaccessible';
      if (user.current_mode === 'public') return 'mode_mismatch';
      return 'accessible';
    }

    case 'mixed': {
      // Mirrors gate rows 9-13. Mixed chargers accept any driver
      // except different-fleet drivers in 'fleet' mode (they can't
      // bill THIS fleet, but they can flip to public). Different-
      // fleet in fleet mode → mode_mismatch.
      if (!userInFleet) return 'accessible';
      if (!sameFleet) {
        return user.current_mode === 'fleet' ? 'mode_mismatch' : 'accessible';
      }
      return 'accessible';
    }
  }
}

/**
 * The remediation reason a `mode_mismatch` marker should surface in
 * its info card. Matches the Phase 10 banner's reason codes so the
 * info-card "Switch to …" CTA can route through the same hook.
 *
 * Returns null when the accessibility resolves to anything other than
 * mode_mismatch — callers should branch on that case explicitly.
 */
export function modeMismatchTarget(
  charger: MapChargerInput,
  user: MapUserInput,
): 'fleet' | 'public' | null {
  const accessibility = resolveChargerAccessibility(charger, user);
  if (accessibility !== 'mode_mismatch') return null;
  // The mismatch direction is the OPPOSITE of the user's current
  // mode (a same-fleet driver in public mode at a fleet_only charger
  // needs to switch TO fleet; a fleet-mode driver at a public charger
  // needs to switch TO public).
  return user.current_mode === 'fleet' ? 'public' : 'fleet';
}
